import { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import { Col, Row, Button, Tooltip } from 'reactstrap';
import dayjs from 'dayjs';

import '../css/patientHistorySummary.css';
import withPermission from "@/components/common/withPermission";
import Mhd2Permissions from "@/constants/permissions";
import { useAppSelector } from "@/store";
import withConfigOnOff from "@/components/common/withConfigOnOff";
import { PatientInfoTabName } from "../patientDetailsView";
import { useActiveTab } from "./activeTabContext";
import { compose } from "redux";

// Main Section
const PatientHistorySummary: React.FC = () => {
    const appointmentHistoryRecords = useSelector((state: any) => state.activePatient.history);
    const careOrders = useSelector((state: any) => state.activePatient.careOrderHistory);
    const openWorkflows = useSelector((state: any) => state.activePatient.openWorkflows);

    const [upcomingAppointmentsCount, setUpcomingAppointmentsCount] = useState(0);
    const [recentlyBookedCount, setRecentlyBookedCount] = useState(0);
    const [openCareOrdersCount, setOpenCareOrdersCount] = useState(0);
    const [openWorkflowsCount, setOpenWorkflowsCount] = useState(0);

    const openCareOrders = careOrders?.filter((careOrder: any) =>
        careOrder.statusCode !== "C" &&
        (!careOrder.expirationDate || dayjs(careOrder.expirationDate) >= dayjs())
    );
    const recentlyBookedAppointments = appointmentHistoryRecords?.filter((appointment: any) =>
        dayjs(appointment.createDateTime) >= dayjs().subtract(7, "day") &&
        appointment.cancellationDateTime === null
    );
    const upcomingAppointments = appointmentHistoryRecords?.filter((appointment: any) =>
        dayjs(appointment.appointmentDate) >= dayjs() &&
        appointment.cancellationDateTime === null
    );
    const unbookedWorkflows = openWorkflows?.filter((workflow: any) => !workflow.appointmentDate);

    useEffect(() => {
        setOpenCareOrdersCount(openCareOrders?.length);
        setUpcomingAppointmentsCount(upcomingAppointments?.length);
        setOpenWorkflowsCount(unbookedWorkflows?.length);
        setRecentlyBookedCount(recentlyBookedAppointments?.length);
    }, [openCareOrders, upcomingAppointments, unbookedWorkflows, recentlyBookedAppointments]);

    return (
        <div>
            <div className="col-lg-12">
                <Row className="patientHistorySummaryRow">
                    <UpcomingAppointmentSummary
                        upcomingAppointmentsCount={upcomingAppointmentsCount}
                    />
                    <RecentlyBookedAppointmentSummary
                        recentlyBookedCount={recentlyBookedCount}
                    />
                    <CareOrderSummary
                        openCareOrdersCount={openCareOrdersCount}
                    />
                    <DecisionSupportWorkflowSummary
                        openWorkflowsCount={openWorkflowsCount}
                    />
                </Row>
                <br />
            </div>
        </div>
    );
};

export default PatientHistorySummary;

// Common Base Component
interface PatientHistorySummarySectionBaseProps {
    label: string;
    count: number;
    containerId?: string;
    tooltipText?: string;
    tabName: PatientInfoTabName;
}

const PatientHistorySummarySectionBase: React.FC<PatientHistorySummarySectionBaseProps> = ({
    label,
    count,
    containerId,
    tooltipText,
    tabName,
}) => {
    const [tooltipOpen, setTooltipOpen] = useState(false);
    const toggleTooltip = () => setTooltipOpen(!tooltipOpen);
    const { setActiveTab } = useActiveTab();
    return (
        <>
            {tooltipText && containerId && (
                <Tooltip placement="top-start" isOpen={tooltipOpen} target={containerId} toggle={toggleTooltip}>
                    {tooltipText}
                </Tooltip>
            )}
            <Col className="patientHistorySummaryCol" id={containerId}>
                <p className="summary-label">{label}</p>
                <Row>
                    <Col className="countCol" lg="2" xxl="1">
                        <h3>{count}</h3>
                    </Col>
                    <Col className="viewBtnCol">
                        <Button className="btnView" onClick={() => setActiveTab({ name: tabName, reloadTabContent: true })}>
                            View
                        </Button>
                    </Col>
                </Row>
            </Col>
        </>
    );
};

// Upcoming Appointment Summary
interface UpcomingAppointmentSummaryProps {
    upcomingAppointmentsCount: number;
}
const UpcomingAppointmentSummaryComponent: React.FC<UpcomingAppointmentSummaryProps> = (props) => {
    return (
        <PatientHistorySummarySectionBase
            label="UPCOMING APPOINTMENTS"
            count={props.upcomingAppointmentsCount}
            tabName={PatientInfoTabName.upcomingAppointment}
        />
    );
};
const UpcomingAppointmentSummary: React.FC<UpcomingAppointmentSummaryProps> = compose(
    withPermission(Mhd2Permissions.appointmentView),
    withConfigOnOff((state) => state.config.patient.isPatientHistoryEnabled)
)(UpcomingAppointmentSummaryComponent) as React.FC<UpcomingAppointmentSummaryProps>;

// Recently Booked Appointment Summary
interface RecentlyBookedAppointmentSummaryProps {
    recentlyBookedCount: number;
}
const RecentlyBookedAppointmentSummaryComponent: React.FC<RecentlyBookedAppointmentSummaryProps> = (props) => {
    return (
        <PatientHistorySummarySectionBase
            label="RECENTLY BOOKED"
            count={props.recentlyBookedCount}
            tabName={PatientInfoTabName.appointmentHistory}
            containerId="recentlyBookedSummaryCol"
            tooltipText="Booked in last 7 days"
        />
    );
};
const RecentlyBookedAppointmentSummary: React.FC<RecentlyBookedAppointmentSummaryProps> = compose(
    withPermission(Mhd2Permissions.appointmentView),
    withConfigOnOff((state) => state.config.patient.isPatientHistoryEnabled)
)(RecentlyBookedAppointmentSummaryComponent) as React.FC<RecentlyBookedAppointmentSummaryProps>;

// Care Order Summary
interface CareOrderSummaryProps {
    openCareOrdersCount: number;
}
const CareOrderSummaryComponent: React.FC<CareOrderSummaryProps> = (props) => {
    return (
        <PatientHistorySummarySectionBase
            label="OPEN CARE ORDERS"
            count={props.openCareOrdersCount}
            tabName={PatientInfoTabName.careOrderHistory}
        />
    );
};
const CareOrderSummary: React.FC<CareOrderSummaryProps> = compose(
    withPermission(Mhd2Permissions.orderView),
    withConfigOnOff((state) => state.config.patient.isPatientCareOrderHistoryEnabled)
)(CareOrderSummaryComponent) as React.FC<CareOrderSummaryProps>;

// Decision Support Workflow Summary
interface DecisionSupportWorkflowSummaryProps {
    openWorkflowsCount: number;
}
const DecisionSupportWorkflowSummaryComponent: React.FC<DecisionSupportWorkflowSummaryProps> = (props) => {
    return (
        <PatientHistorySummarySectionBase
            label="OPEN WORKFLOWS"
            count={props.openWorkflowsCount}
            tabName={PatientInfoTabName.decisionSupportWorkflowHistory}
        />
    );
};
const DecisionSupportWorkflowSummary: React.FC<DecisionSupportWorkflowSummaryProps> = compose(
    withPermission(Mhd2Permissions.appointmentView),
    withConfigOnOff((state) => state.config.patient.isDecisionSupportWorkflowHistoryEnabled)
)(DecisionSupportWorkflowSummaryComponent) as React.FC<DecisionSupportWorkflowSummaryProps>;